import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CurrentContextService } from '../../core/context/current-context.service';
import { liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogOffering, CatalogService, CatalogWarehouse } from './catalog.service';
import { InventoryCostingApiService, InventoryCostLayer } from './inventory-costing-api.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-cost-layers',
  imports: [AinPageComponent, AinStateComponent, InfoHintComponent, FormsModule, TableModule, ButtonModule, SelectModule, CheckboxModule],
  template: `
    <ain-page [title]="t('catalog.costing.layers.title')" [description]="t('catalog.costing.layers.description')">
      <ain-info-hint page-title-addon
        [titleKey]="'catalog.costing.layers.help.title'"
        [descriptionKey]="'catalog.costing.layers.help.description'"
        [bulletKeys]="['catalog.costing.layers.help.bullets.fifo', 'catalog.costing.layers.help.bullets.open']" />
      <span class="live-status" [class]="liveUpdateService.connectionState()">{{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}</span>

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('catalog.costing.states.noPermission')" [message]="t('catalog.costing.states.noPermissionMessage')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="load()"></button>
          </div>
          <div class="filter-grid">
            <label>
              <span>{{ t('catalog.inventory.offeringId') }}</span>
              <p-select [options]="offeringOptions()" [(ngModel)]="filters.offeringId" optionLabel="label" optionValue="value" [showClear]="true" [filter]="true" appendTo="body" (ngModelChange)="load()" />
            </label>
            <label>
              <span>{{ t('catalog.inventory.warehouseId') }}</span>
              <p-select [options]="warehouseOptions()" [(ngModel)]="filters.warehouseId" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body" (ngModelChange)="load()" />
            </label>
            <label class="check">
              <p-checkbox [binary]="true" [(ngModel)]="filters.openOnly" (ngModelChange)="load()" />
              <span>{{ t('catalog.costing.layers.openOnly') }}</span>
            </label>
          </div>

          @if (rows().length === 0) {
            <ain-state type="empty" [title]="t('catalog.costing.layers.empty')" />
          } @else {
            <p-table [value]="rows()" [paginator]="true" [rows]="20" dataKey="id">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('catalog.inventory.offeringId') }}</th>
                  <th>{{ t('catalog.inventory.warehouseId') }}</th>
                  <th>{{ t('catalog.costing.layers.remainingQty') }}</th>
                  <th>{{ t('catalog.costing.layers.unitCost') }}</th>
                  <th>{{ t('catalog.costing.layers.receivedAt') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ resolveOffering(row.offeringId) }}</button></td>
                  <td>{{ resolveWarehouse(row.warehouseId) }}</td>
                  <td>{{ row.remainingQuantity }} / {{ row.originalQuantity }}</td>
                  <td>{{ row.unitCost }} {{ row.currencyCode }}</td>
                  <td>{{ row.receivedAtUtc }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; background: #fff; }
    .toolbar { margin-bottom: 10px; }
    .filter-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; margin-bottom: 12px; }
    .filter-grid label { display: grid; gap: 6px; }
    .check { display: flex; align-items: center; gap: 8px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .live-status { font-size: 0.8rem; color: #6b7280; display: block; margin-bottom: 8px; }
    .live-status.connected { color: #166534; }
  `]
})
export class InventoryCostLayersComponent implements OnInit {
  private readonly costingApi = inject(InventoryCostingApiService);
  private readonly catalog = inject(CatalogService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;

  readonly loading = signal(true);
  readonly permissionDenied = signal(false);
  readonly rows = signal<InventoryCostLayer[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);

  filters = { offeringId: '', warehouseId: '', openOnly: true };

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.catalog.listOfferings().subscribe({ next: o => this.offerings.set(o.filter(x => x.inventoryEnabled)) });
    this.catalog.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    this.load();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.costingApi.listCostLayers({
      offeringId: this.filters.offeringId || undefined,
      warehouseId: this.filters.warehouseId || undefined,
      openOnly: this.filters.openOnly || undefined
    }).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.rows.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.loading.set(false);
      }
    });
  }

  openDetail(id: string): void {
    void this.router.navigate(['/inventory/costing/layers', id]);
  }

  offeringOptions = () => this.offerings().map(o => ({ label: `${o.code} — ${o.title}`, value: o.id }));
  warehouseOptions = () => this.warehouses().map(w => ({ label: `${w.code} — ${w.name}`, value: w.id }));

  resolveOffering(id: string): string {
    const o = this.offerings().find(x => x.id === id);
    return o ? `${o.code} — ${o.title}` : '-';
  }

  resolveWarehouse(id: string): string {
    const w = this.warehouses().find(x => x.id === id);
    return w ? `${w.code} — ${w.name}` : '-';
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
